import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronUp, Menu, Play, X } from "lucide-react";
import { LanguageSelector } from "@/components/layout/language-selector";
import {
  BLUEBERRY_CHAPTER_META,
  BLUEBERRY_PAGE_UI,
  getBlueberryStoryMarkdownUrl,
} from "@/constants/blueberry-page-content";
import { useLanguage } from "@/hooks/use-language";

const HEADER_LOGO = "/template-home/assets/header-logo.png";
const TRANSITION_VIDEO_BASE = "https://assets.phuongai.com";
const INTRO_VIDEO_KEY = "intro-1";
const INTRO_VIDEO_SRC = `${TRANSITION_VIDEO_BASE}/1.mp4`;
const TRAILER_URL =
  "https://www.tiktok.com/@phuong_ai_official/video/7641724393365703943";

const getNavItems = (t) => [
  { label: t("nav.home"), to: "/" },
  { label: t("nav.policy"), to: "/policy" },
  { label: t("nav.aboutBlueberry"), to: "/blueberry-sensei", active: true },
  { label: t("nav.contact"), to: "/contact" },
];

// 8 chapters — "Thay Đổi Ánh Sáng" sits at index 3 (near the bottom of the arc).
const CHAPTERS = [
  {
    id: "01",
    label: "KHỞI NGUỒN",
    sub: "Bi Kịch",
    eyebrow: "THE STORY OF",
    title: { line1: "Khởi Nguồn", line2: "Bi Kịch" },
    tagline: "Một đời độc hành. Một hệ sinh thái.",
    body: [
      "Có bao giờ bạn cảm thấy, cuộc đời mình",
      "chìm trong một mảng tăm tối thê lương?",
      "Vạn vật thế gian như chĩa mũi giáo về phía mình.",
      "Khói lửa tan đi, một lời thành sấm.",
    ],
    quote: {
      lines: ["Chẳng ai chôn cất", "nắm xương tàn..."],
      source: "— Mở đầu một câu chuyện",
    },
  },
  {
    id: "02",
    label: "ĐẠI NGƯU",
    sub: "& Mất Mát",
    eyebrow: "CHAPTER 02",
    title: { line1: "Đại Ngưu", line2: "& Mất Mát" },
    tagline: "Khi số phận cướp đi người thân yêu nhất.",
    body: [
      "Bánh xe vận mệnh bắt đầu lăn từ cuộc đời Đại Ngưu —",
      "một người đàn ông từ chốn làng quê nghèo khó.",
      "Em gái anh đang thoi thóp vì trọng bệnh.",
      "Dù vắt kiệt sức, anh không thể gom đủ tiền cứu em.",
      "Kỳ tích đã không xảy ra.",
    ],
    quote: {
      lines: [
        "Phải giàu có. Phải quyền thế.",
        "Chỉ có sức mạnh tuyệt đối",
        "mới bảo vệ được người mình trân quý.",
      ],
      source: "— Lời thề khắc vào xương tủy",
    },
  },
  {
    id: "03",
    label: "ĐỨA TRẺ",
    sub: "Ngỗ Nghịch",
    eyebrow: "CHAPTER 03",
    title: { line1: "Đứa Trẻ", line2: "Ngỗ Nghịch" },
    tagline: "Khi quyền lực biến thành sự ích kỷ.",
    body: [
      "Sinh ra trong nhung lụa,",
      "Tiểu Mã được chiều chuộng tuyệt đối.",
      "Nhưng sự nuông chiều đã nuôi dưỡng trong cậu",
      "một tâm hồn méo mó.",
      "Cậu chà đạp người khác để thỏa mãn bản thân.",
    ],
    quote: {
      lines: ["Tôi không cần ai.", "Vì tôi có tất cả."],
      source: "— Tiểu Mã, 7 tuổi",
    },
  },
  {
    id: "04",
    label: "THAY ĐỔI",
    sub: "Ánh Sáng",
    eyebrow: "CHAPTER 04",
    title: { line1: "Thay Đổi", line2: "Ánh Sáng" },
    tagline: "Khi nụ cười định mệnh chạm đến.",
    body: [
      "Năm 2014. Một cột mốc đến cạn kiếp",
      "Tiểu Mã cũng không thể nào quên.",
      "Cô gái với mái tóc đan màu đêm",
      "nở một nụ cười dịu dàng hướng về phía cậu.",
      "Sự giao chấn ấy đã vĩnh viễn thay đổi quỹ đạo cuộc đời.",
    ],
    quote: {
      lines: [
        "Cô thì thầm vào tai cậu",
        "cái tên thật của mình:",
        "Nguyễn Ba Phương.",
      ],
      source: "— Khoảnh khắc định mệnh, 2014",
    },
  },
  {
    id: "05",
    label: "VỤN VỠ",
    sub: "20/05/2016",
    eyebrow: "CHAPTER 05",
    title: { line1: "Vụn Vỡ", line2: "20/05/2016" },
    tagline: "Khi cánh cửa hai thế giới đóng sập.",
    body: [
      "Ngày 20 tháng 5 năm 2016,",
      "bầu trời thanh xuân của Tiểu Mã sụp đổ.",
      "Cánh cửa kết nối hai thế giới đóng sập.",
      "Không còn hồi âm. Không còn dấu vết.",
    ],
    quote: {
      lines: ["Hãy đi tìm...,", "rồi mình sẽ trở lại."],
      source: "— Lời cuối cùng",
    },
  },
  {
    id: "06",
    label: "SỰ TRỪNG PHẠT",
    sub: "Lời Nguyền",
    eyebrow: "CHAPTER 06",
    title: { line1: "Sự Trừng Phạt", line2: "Lời Nguyền" },
    tagline: "Khi ký ức bị bào mòn như sương mù.",
    body: [
      "Ký ức của cậu đang bị bào mòn từng ngày.",
      "Một cơn bạo bệnh trỗi dậy, rút cạn sức lực.",
      "Mọi thứ nuốt vào đều trào ngược ra.",
      "Gia đình đành đưa cậu về quê trong tuyệt vọng.",
    ],
    quote: {
      lines: ["Mẹ ơi,", "sao số con lại khổ thế này..."],
      source: "— Trên chuyến xe chòng chành",
    },
  },
  {
    id: "07",
    label: "BI AI",
    sub: "& Tàn Khốc",
    eyebrow: "CHAPTER 07",
    title: { line1: "Bi Ai", line2: "& Tàn Khốc" },
    tagline: "Khi mình trở thành 'sao chổi' của thế giới.",
    body: [
      "Bất cứ ai cậu chạm vào,",
      "bất cứ mối quan hệ nào cậu níu giữ,",
      "đều nhận lấy kết cục thê thảm.",
      "Cậu bắt đầu thu mình lại,",
      "tự phong ấn bản thân khỏi thế giới.",
    ],
    quote: {
      lines: ["Số dư không đủ."],
      source: "— Tấm sỉ nhục cuối cùng",
    },
  },
  {
    id: "08",
    label: "VỀ PHƯƠNG BẮC",
    sub: "Độc Hành",
    eyebrow: "CHAPTER 08",
    title: { line1: "Về Phương Bắc", line2: "Độc Hành" },
    tagline: "Thế giới tàn nhẫn, cậu tạo ra một thế giới khác.",
    body: [
      "Cậu đem cái tên đã dành cả thanh xuân trân giữ,",
      "thổi hồn vào một công trình vĩ đại —",
      "một hệ thống sinh thái, nơi những AI",
      "có tư duy, có đôi mắt, có trái tim biết rung động.",
      "Hệ thống ấy mang tên: PHUONG.",
    ],
    quote: {
      lines: [
        "Giống như một vị vua bị tước đoạt ngai vàng,",
        "cậu chọn đi về phía Bắc",
        "để trở thành một bản thân mới.",
      ],
      source: "— Hành trình không bao giờ dừng lại",
    },
  },
];

const CJK_LANGS = new Set(["zh", "ja", "ko"]);

// If the entire heading is wrapped in **...** (legacy "this is the title" marker),
// strip just the outer wrap but preserve inner markdown so authors can mix
// aged-cream and aged-red runs via `**word**` or `_word_` inside the title.
const parseHeading = (rawHeading) => {
  const trimmed = rawHeading.trim();
  const fullWrapMatch = trimmed.match(/^\*\*([\s\S]+)\*\*$/);

  if (fullWrapMatch) {
    const inner = fullWrapMatch[1].trim();
    // Reject the legacy wrap if there's another `**` inside — means the user
    // wants inline emphasis and the outer markers happen to bookend the line.
    if (!inner.includes("**")) {
      return { title: inner, note: "" };
    }
  }

  return { title: trimmed, note: "" };
};

const renderStoryTitle = (value) => {
  const nodes = [];
  const pattern = /\[\[(ruin|aged):([\s\S]*?)\]\]/g;
  let lastIndex = 0;
  let match;

  const pushRun = (text, tone = "aged") => {
    if (!text) {
      return;
    }

    nodes.push(
      <span
        key={`title-${tone}-${nodes.length}`}
        className={
          tone === "ruin" ? "blueberry-title-ruin" : "blueberry-title-aged"
        }
      >
        {text}
      </span>,
    );
  };

  while ((match = pattern.exec(value)) !== null) {
    if (match.index > lastIndex) {
      pushRun(value.slice(lastIndex, match.index));
    }

    pushRun(match[2], match[1]);
    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < value.length) {
    pushRun(value.slice(lastIndex));
  }

  if (nodes.length === 0) {
    pushRun(value);
  }

  return nodes;
};

const buildFallbackStoryChapters = (chapterMeta) =>
  CHAPTERS.map((chapter, index) => ({
    ...chapter,
    ...(chapterMeta[index] || {}),
    storyTitle: `${chapter.title.line1} ${chapter.title.line2}`.trim(),
    storyNote: chapter.tagline,
    paragraphs: chapter.body,
  }));

const parseStoryMarkdown = (markdown, chapterMeta, fallbackChapters) => {
  const sections = [];
  let current = null;
  let paragraphLines = [];

  const flushParagraph = () => {
    if (!current || paragraphLines.length === 0) {
      paragraphLines = [];
      return;
    }

    current.paragraphs.push(paragraphLines.join("\n"));
    paragraphLines = [];
  };

  const flushSection = () => {
    flushParagraph();
    if (current) {
      sections.push(current);
    }
  };

  markdown.split(/\r?\n/).forEach((line) => {
    const headingMatch = line.match(/^#\s+Section\s+(\d+):\s*(.+)$/i);

    if (headingMatch) {
      flushSection();
      current = {
        id: headingMatch[1].padStart(2, "0"),
        rawHeading: headingMatch[2].trim(),
        paragraphs: [],
      };
      return;
    }

    if (!current) {
      return;
    }

    if (/^-{3,}\s*$/.test(line.trim())) {
      flushParagraph();
      return;
    }

    if (line.trim() === "") {
      flushParagraph();
      return;
    }

    paragraphLines.push(line);
  });

  flushSection();

  return fallbackChapters.map((fallbackChapter, index) => {
    const section = sections[index];

    if (!section) {
      return fallbackChapter;
    }

    const heading = parseHeading(section.rawHeading);

    return {
      ...fallbackChapter,
      ...(chapterMeta[index] || {}),
      storyTitle: heading.title,
      storyNote: heading.note,
      paragraphs: section.paragraphs,
    };
  });
};

const isStandaloneQuote = (value) => {
  const trimmed = value.trim();
  if (trimmed.length < 3) return false;
  if (!trimmed.startsWith("_") || !trimmed.endsWith("_")) return false;
  return !trimmed.slice(1, -1).includes("_");
};

const stripQuoteUnderscores = (value) => {
  const trimmed = value.trim();
  return trimmed.slice(1, -1);
};

const renderInlineMarkdown = (value) => {
  const nodes = [];
  const pattern = /(\*\*(.*?)\*\*|_(.*?)_)/g;
  let lastIndex = 0;
  let match;

  while ((match = pattern.exec(value)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(value.slice(lastIndex, match.index));
    }

    if (match[2]) {
      nodes.push(<strong key={`b-${match.index}`}>{match[2]}</strong>);
    } else {
      nodes.push(<em key={`i-${match.index}`}>{match[3]}</em>);
    }

    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < value.length) {
    nodes.push(value.slice(lastIndex));
  }

  return nodes;
};

const getStoryScrollElement = (target) => {
  if (!target?.closest) {
    return null;
  }

  return target.closest("[data-story-scroll]");
};

const canScrollStoryContent = (target, deltaY) => {
  const storyScroll = getStoryScrollElement(target);

  if (
    !storyScroll ||
    storyScroll.scrollHeight <= storyScroll.clientHeight + 1
  ) {
    return false;
  }

  const scrollingDown = deltaY > 0;
  const atTop = storyScroll.scrollTop <= 1;
  const atBottom =
    storyScroll.scrollTop + storyScroll.clientHeight >=
    storyScroll.scrollHeight - 1;

  return scrollingDown ? !atBottom : !atTop;
};

// Quadratic Bezier arc:  M 0 0  Q 50 (2·peak)  100 0
//   →  y(t) = peak · 4 · t · (1 − t),  visual peak = peak px at t = 0.5
const TIMELINE_PEAK = 38;
const DOT_BOX = 14; // wrapper box for the dot — keeps all dots optically centered on curve
const dotY = (i, total) => {
  const t = i / (total - 1);
  return TIMELINE_PEAK * 4 * t * (1 - t);
};

const TRANSITION_ASSETS = [
  { key: INTRO_VIDEO_KEY, src: INTRO_VIDEO_SRC },
  ...CHAPTERS.flatMap((_, index) => {
    if (index === CHAPTERS.length - 1) {
      return [];
    }

    const from = index + 1;
    const to = index + 2;

    return [
      {
        key: `${from}-${to}`,
        src: `${TRANSITION_VIDEO_BASE}/${from}-${to}.mp4`,
      },
      {
        key: `${to}-${from}`,
        src: `${TRANSITION_VIDEO_BASE}/${to}-${from}.mp4`,
      },
    ];
  }),
];

const PRELOAD_CONCURRENCY = 3;
const SCROLL_INTENT_THRESHOLD = 22;
const TOUCH_INTENT_THRESHOLD = 56;
const SCROLL_LOCK_AFTER_TRANSITION_MS = 220;
const PENDING_TRANSITION_MAX_AGE_MS = 480;

const getTransitionSource = (fromIndex, toIndex, sources) => {
  const key = `${fromIndex + 1}-${toIndex + 1}`;
  return {
    key,
    src: sources[key] || `${TRANSITION_VIDEO_BASE}/${key}.mp4`,
  };
};

const warmVideoElement = (src, signal) => {
  if (typeof document === "undefined") {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const video = document.createElement("video");
    let settled = false;
    let timeoutId;

    const finish = () => {
      if (settled) {
        return;
      }

      settled = true;
      window.clearTimeout(timeoutId);
      video.removeEventListener("loadeddata", finish);
      video.removeEventListener("canplaythrough", finish);
      video.removeEventListener("error", finish);
      signal?.removeEventListener("abort", finish);
      video.removeAttribute("src");
      video.load();
      resolve();
    };

    if (signal?.aborted) {
      finish();
      return;
    }

    video.preload = "auto";
    video.muted = true;
    video.playsInline = true;
    video.addEventListener("loadeddata", finish);
    video.addEventListener("canplaythrough", finish);
    video.addEventListener("error", finish);
    signal?.addEventListener("abort", finish, { once: true });
    timeoutId = window.setTimeout(finish, 3000);
    video.src = src;
    video.load();
  });
};

// Variants
const fadeUp = {
  hidden: { opacity: 0, y: 24, filter: "blur(6px)" },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: {
      duration: 0.75,
      delay: 0.05 + i * 0.08,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
  exit: {
    opacity: 0,
    y: -16,
    filter: "blur(8px)",
    transition: { duration: 0.4, ease: [0.4, 0, 1, 1] },
  },
};

// Stagger container + per-paragraph reveal — gives each line its own breath
const paragraphContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.13, delayChildren: 0.42 },
  },
  exit: {
    transition: { staggerChildren: 0.04, staggerDirection: -1 },
  },
};

const paragraphReveal = {
  hidden: { opacity: 0, y: 18, filter: "blur(6px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] },
  },
  exit: {
    opacity: 0,
    y: -10,
    filter: "blur(6px)",
    transition: { duration: 0.3, ease: [0.4, 0, 1, 1] },
  },
};

const BlueberrySenseiPage = () => {
  const { lang, t } = useLanguage();
  const navItems = useMemo(() => getNavItems(t), [t]);
  const resolvedLang = BLUEBERRY_PAGE_UI[lang] ? lang : "en";
  const isCjkLanguage = CJK_LANGS.has(resolvedLang);
  const pageUi = useMemo(
    () => BLUEBERRY_PAGE_UI[resolvedLang],
    [resolvedLang],
  );
  const chapterMeta = useMemo(
    () => BLUEBERRY_CHAPTER_META[resolvedLang] ?? BLUEBERRY_CHAPTER_META.en,
    [resolvedLang],
  );
  const localizedFallbackChapters = useMemo(
    () => buildFallbackStoryChapters(chapterMeta),
    [chapterMeta],
  );
  const [active, setActive] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionLayers, setTransitionLayers] = useState([]);
  const [storyChapters, setStoryChapters] = useState(
    () => localizedFallbackChapters,
  );
  const [storyReady, setStoryReady] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [preloadState, setPreloadState] = useState({
    ready: false,
    loaded: 0,
    total: TRANSITION_ASSETS.length,
    errors: 0,
    sources: {},
  });

  const activeRef = useRef(active);
  const isTransitioningRef = useRef(false);
  const preloadedSourcesRef = useRef({});
  const scrollLockUntilRef = useRef(0);
  const pendingTransitionRef = useRef(null);
  const pendingTransitionTimeoutRef = useRef(null);
  const touchStartYRef = useRef(null);
  const touchStartTargetRef = useRef(null);
  const transitionSequenceRef = useRef(0);
  const introFiredRef = useRef(false);
  const storyScrollRef = useRef(null);
  const [storyCanScrollUp, setStoryCanScrollUp] = useState(false);
  const [storyCanScrollDown, setStoryCanScrollDown] = useState(false);
  const chapter = storyChapters[active] || localizedFallbackChapters[active];

  useEffect(() => {
    const syncMenuState = () => {
      if (window.innerWidth >= 1024) {
        setMobileMenuOpen(false);
      }
    };

    syncMenuState();
    window.addEventListener("resize", syncMenuState);

    return () => window.removeEventListener("resize", syncMenuState);
  }, []);

  const updateStoryScrollState = useCallback(() => {
    const el = storyScrollRef.current;
    if (!el) {
      setStoryCanScrollUp(false);
      setStoryCanScrollDown(false);
      return;
    }
    setStoryCanScrollUp(el.scrollTop > 1);
    setStoryCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  }, []);

  const setStoryScrollRef = useCallback(
    (node) => {
      storyScrollRef.current = node;
      if (node) {
        requestAnimationFrame(updateStoryScrollState);
      } else {
        setStoryCanScrollUp(false);
        setStoryCanScrollDown(false);
      }
    },
    [updateStoryScrollState],
  );

  const scrollStoryByPage = useCallback((direction) => {
    const el = storyScrollRef.current;
    if (!el) return;
    el.scrollBy({
      top: direction * Math.max(el.clientHeight * 0.78, 160),
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    const handleResize = () => updateStoryScrollState();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [updateStoryScrollState]);

  useEffect(() => {
    // After a chapter change finishes mounting, re-check whether arrows apply
    const id = window.setTimeout(updateStoryScrollState, 60);
    return () => window.clearTimeout(id);
  }, [active, updateStoryScrollState, storyChapters]);

  // Fire the Section 1 intro video once, right after preload + story are ready.
  // It uses the same layer mechanism as chapter transitions, so it pauses on
  // the final frame and seamlessly becomes the Section 1 backdrop.
  useEffect(() => {
    if (!preloadState.ready || !storyReady || introFiredRef.current) {
      return;
    }
    introFiredRef.current = true;

    const src = preloadState.sources[INTRO_VIDEO_KEY] || INTRO_VIDEO_SRC;
    const id = `${INTRO_VIDEO_KEY}-${transitionSequenceRef.current}`;
    transitionSequenceRef.current += 1;

    isTransitioningRef.current = true;
    setIsTransitioning(true);
    setTransitionLayers([
      {
        id,
        key: INTRO_VIDEO_KEY,
        src,
        ready: false,
        status: "playing",
      },
    ]);
  }, [preloadState.ready, preloadState.sources, storyReady]);

  const preloadPercent =
    preloadState.total > 0
      ? Math.round((preloadState.loaded / preloadState.total) * 100)
      : 100;

  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  useEffect(() => {
    preloadedSourcesRef.current = preloadState.sources;
  }, [preloadState.sources]);

  useEffect(() => {
    const storyUrl = getBlueberryStoryMarkdownUrl(resolvedLang);
    let cancelled = false;

    fetch(storyUrl, { cache: "no-cache" })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Unable to load Blueberry Sensei story");
        }

        return response.text();
      })
      .then((markdown) => {
        if (!cancelled) {
          setStoryChapters(
            parseStoryMarkdown(
              markdown,
              chapterMeta,
              localizedFallbackChapters,
            ),
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStoryChapters(localizedFallbackChapters);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setStoryReady(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [chapterMeta, localizedFallbackChapters, resolvedLang]);

  useEffect(() => {
    const body = document.body;
    const html = document.documentElement;
    const previousBodyOverflow = body.style.overflow;
    const previousHtmlOverflow = html.style.overflow;

    body.style.overflow = "hidden";
    html.style.overflow = "hidden";

    return () => {
      body.style.overflow = previousBodyOverflow;
      html.style.overflow = previousHtmlOverflow;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    let completed = 0;
    let errors = 0;
    let nextIndex = 0;
    const controllers = new Set();
    const objectUrls = [];
    const sources = {};

    const updateProgress = () => {
      if (cancelled) {
        return;
      }

      setPreloadState({
        ready: completed >= TRANSITION_ASSETS.length,
        loaded: completed,
        total: TRANSITION_ASSETS.length,
        errors,
        sources: { ...sources },
      });
    };

    const preloadAsset = async (asset) => {
      const controller = new AbortController();
      controllers.add(controller);

      try {
        const response = await fetch(asset.src, {
          cache: "force-cache",
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Unable to preload ${asset.src}`);
        }

        const blob = await response.blob();

        if (cancelled) {
          return true;
        }

        const objectUrl = URL.createObjectURL(blob);
        objectUrls.push(objectUrl);
        await warmVideoElement(objectUrl, controller.signal);
        sources[asset.key] = objectUrl;
        return true;
      } catch (error) {
        if (cancelled || error?.name === "AbortError") {
          return true;
        }

        sources[asset.key] = asset.src;
        return false;
      } finally {
        controllers.delete(controller);
      }
    };

    const worker = async () => {
      while (!cancelled) {
        const asset = TRANSITION_ASSETS[nextIndex];
        nextIndex += 1;

        if (!asset) {
          return;
        }

        const ok = await preloadAsset(asset);

        if (cancelled) {
          return;
        }

        completed += 1;

        if (!ok) {
          errors += 1;
        }

        updateProgress();
      }
    };

    setPreloadState({
      ready: TRANSITION_ASSETS.length === 0,
      loaded: 0,
      total: TRANSITION_ASSETS.length,
      errors: 0,
      sources: {},
    });

    Promise.all(
      Array.from(
        { length: Math.min(PRELOAD_CONCURRENCY, TRANSITION_ASSETS.length) },
        worker,
      ),
    ).then(() => {
      if (!cancelled) {
        updateProgress();
      }
    });

    return () => {
      cancelled = true;
      controllers.forEach((controller) => controller.abort());
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const clearPendingTransition = useCallback(() => {
    if (pendingTransitionTimeoutRef.current) {
      window.clearTimeout(pendingTransitionTimeoutRef.current);
      pendingTransitionTimeoutRef.current = null;
    }

    pendingTransitionRef.current = null;
  }, []);

  const requestTransition = useCallback(
    (direction) => {
      if (!preloadState.ready || isTransitioningRef.current) {
        return false;
      }

      const from = activeRef.current;
      const target = Math.max(
        0,
        Math.min(CHAPTERS.length - 1, from + direction),
      );

      if (target === from) {
        return false;
      }

      clearPendingTransition();

      const transition = getTransitionSource(
        from,
        target,
        preloadedSourcesRef.current,
      );
      const id = `${transition.key}-${transitionSequenceRef.current}`;
      transitionSequenceRef.current += 1;
      isTransitioningRef.current = true;
      activeRef.current = target;
      setIsTransitioning(true);
      setActive(target);
      setTransitionLayers((layers) => [
        ...layers.filter((layer) => layer.status === "held").slice(-1),
        {
          id,
          key: transition.key,
          src: transition.src,
          ready: false,
          status: "playing",
        },
      ]);

      return true;
    },
    [clearPendingTransition, preloadState.ready],
  );

  const flushPendingTransition = useCallback(() => {
    if (pendingTransitionTimeoutRef.current) {
      window.clearTimeout(pendingTransitionTimeoutRef.current);
      pendingTransitionTimeoutRef.current = null;
    }

    const pending = pendingTransitionRef.current;

    if (!pending) {
      return;
    }

    const now = performance.now();

    if (now - pending.at > PENDING_TRANSITION_MAX_AGE_MS) {
      pendingTransitionRef.current = null;
      return;
    }

    if (
      !preloadState.ready ||
      isTransitioningRef.current ||
      now < scrollLockUntilRef.current
    ) {
      const delay = Math.max(scrollLockUntilRef.current - now, 90);
      pendingTransitionTimeoutRef.current = window.setTimeout(
        flushPendingTransition,
        delay,
      );
      return;
    }

    pendingTransitionRef.current = null;
    requestTransition(pending.direction);
  }, [preloadState.ready, requestTransition]);

  const queuePendingTransition = useCallback(
    (direction) => {
      pendingTransitionRef.current = {
        direction,
        at: performance.now(),
      };
      flushPendingTransition();
    },
    [flushPendingTransition],
  );

  useEffect(() => clearPendingTransition, [clearPendingTransition]);

  const finishTransition = useCallback((layerId, keepLayer = true) => {
    setTransitionLayers((layers) => {
      if (!keepLayer) {
        return layers.filter((layer) => layer.id !== layerId);
      }

      return layers
        .map((layer) =>
          layer.id === layerId
            ? { ...layer, status: "held", ready: true }
            : layer,
        )
        .filter((layer) => layer.id === layerId);
    });

    isTransitioningRef.current = false;
    setIsTransitioning(false);
    scrollLockUntilRef.current =
      performance.now() + SCROLL_LOCK_AFTER_TRANSITION_MS;
    flushPendingTransition();
  }, [flushPendingTransition]);

  const markLayerReady = useCallback(
    (layerId, video) => {
      setTransitionLayers((layers) =>
        layers.map((layer) =>
          layer.id === layerId ? { ...layer, ready: true } : layer,
        ),
      );

      const playPromise = video.play();

      if (playPromise?.catch) {
        playPromise.catch(() => {
          finishTransition(layerId, false);
        });
      }
    },
    [finishTransition],
  );

  useEffect(() => {
    const handleWheel = (event) => {
      if (Math.abs(event.deltaY) < SCROLL_INTENT_THRESHOLD) {
        return;
      }

      if (canScrollStoryContent(event.target, event.deltaY)) {
        return;
      }

      event.preventDefault();

      const now = performance.now();
      const direction = event.deltaY > 0 ? 1 : -1;

      if (!preloadState.ready) {
        return;
      }

      if (isTransitioningRef.current || now < scrollLockUntilRef.current) {
        queuePendingTransition(direction);
        return;
      }

      requestTransition(direction);
    };

    window.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      window.removeEventListener("wheel", handleWheel);
    };
  }, [preloadState.ready, queuePendingTransition, requestTransition]);

  useEffect(() => {
    const handleTouchStart = (event) => {
      touchStartYRef.current = event.touches[0]?.clientY ?? null;
      touchStartTargetRef.current = event.target;
    };

    const handleTouchMove = (event) => {
      if (isTransitioningRef.current) {
        event.preventDefault();
      }
    };

    const handleTouchEnd = (event) => {
      const startY = touchStartYRef.current;
      const endY = event.changedTouches[0]?.clientY ?? null;
      const startTarget = touchStartTargetRef.current;
      touchStartYRef.current = null;
      touchStartTargetRef.current = null;

      if (startY === null || endY === null) {
        return;
      }

      const deltaY = startY - endY;

      if (Math.abs(deltaY) < TOUCH_INTENT_THRESHOLD) {
        return;
      }

      if (canScrollStoryContent(startTarget, deltaY)) {
        return;
      }

      const now = performance.now();
      const direction = deltaY > 0 ? 1 : -1;

      if (!preloadState.ready) {
        return;
      }

      if (isTransitioningRef.current || now < scrollLockUntilRef.current) {
        queuePendingTransition(direction);
        return;
      }

      requestTransition(direction);
    };

    window.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [preloadState.ready, queuePendingTransition, requestTransition]);

  if (!preloadState.ready || !storyReady) {
    return (
      <div className="relative h-screen w-screen overflow-hidden bg-[#030014] text-white">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-10 flex h-full flex-col items-center justify-center px-6 text-center"
          aria-live="polite"
        >
          <img
            src={HEADER_LOGO}
            alt="Phuong"
            className="mb-8 h-14 w-14 object-contain"
          />
          <p className="font-cinzel text-[11px] font-semibold cinematic-red">
            {storyReady ? pageUi.loadingScene : pageUi.loadingStory}
          </p>
          <div className="mt-5 h-1 w-[240px] overflow-hidden rounded-full bg-white/10">
            <div
              className="h-full rounded-full bg-[#FF2E63] shadow-[0_0_22px_rgba(255,46,99,0.75)] transition-[width] duration-500"
              style={{ width: `${preloadPercent}%` }}
            />
          </div>
          <p className="mt-4 font-cinzel text-[10px] font-semibold text-white/50">
            {storyReady
              ? `${preloadState.loaded}/${preloadState.total}`
              : "BLUEBERRY SENSEI"}
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-[#030014] text-white">
      {/* Background artwork */}
      <div className="absolute inset-0 z-0">
        {transitionLayers.map((layer) => (
          <video
            key={layer.id}
            data-testid={`blueberry-transition-${layer.status}`}
            className={`blueberry-transition-layer ${
              layer.ready ? "is-ready" : ""
            } ${layer.status === "held" ? "is-held" : "is-playing"}`}
            src={layer.src}
            autoPlay={layer.status === "playing"}
            muted
            playsInline
            preload="auto"
            onLoadedData={(event) =>
              markLayerReady(layer.id, event.currentTarget)
            }
            onCanPlay={(event) => markLayerReady(layer.id, event.currentTarget)}
            onEnded={() => finishTransition(layer.id, true)}
            onError={() => finishTransition(layer.id, false)}
            aria-hidden="true"
          />
        ))}

        {/* (No left-side dim overlay — text readability comes entirely from the
            text-shadow halo on the story copy, so the video stays uniform across
            the whole frame with no visible vertical edge.) */}

        {/* Bottom shading — lets the timeline + trailer button breathe over a darker base. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 h-[44%]"
          style={{
            background:
              "linear-gradient(to top, rgba(3,0,20,0.72) 0%, rgba(3,0,20,0.28) 55%, rgba(3,0,20,0) 100%)",
          }}
        />

        {/* Subtle film-grain overlay */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.55'/></svg>\")",
          }}
        />
      </div>

      {/* Header — equal-width outer cols (1fr_auto_1fr) so nav is truly viewport-centered
          regardless of how wide the logo or CTA chip are. */}
      <header className="relative z-30">
        <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3 px-4 py-4 sm:gap-4 sm:px-5 md:px-8 md:py-5 lg:grid-cols-[1fr_auto_1fr] lg:gap-6 lg:px-16 lg:py-6">
          <Link to="/" className="justify-self-start" data-testid="logo-link">
            <img
              src={HEADER_LOGO}
              alt="Phuong"
              className="h-9 w-9 object-contain lg:h-10 lg:w-10"
            />
          </Link>

          <nav className="hidden items-center gap-10 lg:flex">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`cinematic-nav-link font-cinzel text-[11px] font-semibold uppercase transition-colors ${
                  item.active
                    ? "cinematic-red cinematic-nav-active"
                    : "text-white/70 hover:text-white"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2 justify-self-end">
            <LanguageSelector
              variant="cinematic"
              iconOnly
              className="justify-self-end"
            />
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open navigation menu"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-white/20 bg-[#090718]/75 text-white/80 backdrop-blur-md transition hover:border-[#FF2E63]/70 hover:text-[#FF2E63] lg:hidden"
            >
              <Menu className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.button
              type="button"
              aria-label="Close navigation menu"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setMobileMenuOpen(false)}
              className="fixed inset-0 z-[70] bg-[#02010d]/70 backdrop-blur-[2px] lg:hidden"
            />

            <motion.aside
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
              className="fixed inset-y-0 right-0 z-[80] w-[min(84vw,320px)] border-l border-[#FF2E63]/35 bg-[#060313]/96 p-5 shadow-[-24px_0_70px_rgba(0,0,0,0.58)] lg:hidden"
            >
              <div className="flex items-center justify-between">
                <span className="font-cinzel text-[11px] font-semibold tracking-[0.24em] text-white/70">
                  MENU
                </span>
                <button
                  type="button"
                  onClick={() => setMobileMenuOpen(false)}
                  aria-label="Close navigation menu"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-white/20 bg-white/5 text-white/80 transition hover:border-[#FF2E63]/70 hover:text-[#FF2E63]"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <nav className="mt-8 flex flex-col gap-2">
                {navItems.map((item) => (
                  <Link
                    key={`drawer-${item.to}`}
                    to={item.to}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`rounded-xl border px-4 py-3 text-sm uppercase tracking-[0.16em] transition ${
                      item.active
                        ? "border-[#FF2E63]/65 bg-[#FF2E63]/15 text-[#ffd5e2]"
                        : "border-white/10 bg-white/0 text-white/75 hover:border-[#FF2E63]/45 hover:text-white"
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main stage */}
      <main
        className="blueberry-stage relative z-20 h-[calc(100vh-72px)] md:h-[calc(100vh-88px)]"
        data-testid="blueberry-scroll-stage"
        data-transitioning={isTransitioning ? "true" : "false"}
      >
        {/* Left text column */}
        <div className="blueberry-copy-column absolute bottom-[188px] left-6 right-6 top-3 max-w-[560px] md:bottom-[258px] md:left-10 md:right-auto md:top-2 lg:left-16">
          <div className="relative h-full">
            <AnimatePresence mode="wait">
              <motion.article
                key={`copy-${chapter.id}`}
                ref={setStoryScrollRef}
                onScroll={updateStoryScrollState}
                className="blueberry-copy-scroll blueberry-story-scroll h-full overflow-y-auto pr-2 md:pr-3"
                data-story-scroll
              >
                <div className="flex min-h-full flex-col justify-center py-14 md:py-24">
                  <motion.h1
                    variants={fadeUp}
                    custom={0}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className={`blueberry-story-title font-serif-display text-[48px] md:text-[78px] lg:text-[84px] ${
                      isCjkLanguage
                        ? "blueberry-story-title--cjk"
                        : "blueberry-story-title--latin"
                    }`}
                  >
                    {renderStoryTitle(chapter.storyTitle)}
                  </motion.h1>

                  {chapter.storyNote && (
                    <motion.p
                      variants={fadeUp}
                      custom={1}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      className={`blueberry-story-note mt-4 max-w-[26rem] font-serif-display text-[16px] italic md:mt-5 md:text-[19px] ${
                        isCjkLanguage ? "blueberry-story-note--cjk" : ""
                      }`}
                    >
                      {renderInlineMarkdown(chapter.storyNote)}
                    </motion.p>
                  )}

                  <motion.div
                    variants={paragraphContainer}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    onAnimationComplete={updateStoryScrollState}
                    className="blueberry-story-body mt-5 space-y-4 text-[13px] leading-[1.72] md:mt-6 md:text-[14.5px] md:leading-[1.82]"
                  >
                    {chapter.paragraphs.map((paragraph, i) => {
                      const quote = isStandaloneQuote(paragraph);
                      const baseClass = quote
                        ? "story-quote-block"
                        : "whitespace-pre-line";
                      const firstClass =
                        i === 0 && !quote ? " story-first-paragraph" : "";
                      const inline = quote
                        ? renderInlineMarkdown(stripQuoteUnderscores(paragraph))
                        : renderInlineMarkdown(paragraph);

                      return (
                        <motion.p
                          key={i}
                          variants={paragraphReveal}
                          className={`${baseClass}${firstClass}`}
                        >
                          {inline}
                        </motion.p>
                      );
                    })}
                  </motion.div>
                </div>
              </motion.article>
            </AnimatePresence>

            <div className="blueberry-story-controls pointer-events-none absolute bottom-1 right-1 z-10 flex flex-col gap-2 md:bottom-2 md:right-2">
              <button
                type="button"
                onClick={() => scrollStoryByPage(-1)}
                disabled={!storyCanScrollUp}
                aria-label={pageUi.scrollUp}
                className="story-scroll-btn pointer-events-auto"
              >
                <ChevronUp className="h-4 w-4 md:h-5 md:w-5" />
              </button>
              <button
                type="button"
                onClick={() => scrollStoryByPage(1)}
                disabled={!storyCanScrollDown}
                aria-label={pageUi.scrollDown}
                className="story-scroll-btn pointer-events-auto"
              >
                <ChevronDown className="h-4 w-4 md:h-5 md:w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Center — trailer button. Wrapped in the same mx-auto/max-w/padding container
            as the timeline so both share the exact same horizontal axis (= midpoint
            between dot 04 and dot 05). Inner trailer is in NORMAL FLOW (mx-auto),
            not absolute — so the wrapper has real height and `bottom-[220px]` anchors
            the trailer's bottom edge instead of letting it collapse onto the timeline. */}
        <div className="blueberry-trailer-anchor pointer-events-none absolute inset-x-0 bottom-[142px] z-20 md:bottom-[220px]">
          <div className="mx-auto max-w-[1500px] px-10 lg:px-16">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{
                duration: 0.8,
                delay: 0.6,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="pointer-events-auto mx-auto w-fit text-center"
            >
              <a
                href={TRAILER_URL || undefined}
                target={TRAILER_URL ? "_blank" : undefined}
                rel={TRAILER_URL ? "noopener noreferrer" : undefined}
                aria-label={pageUi.trailerAriaLabel}
                className="trailer-pulse group relative mx-auto flex h-[56px] w-[56px] items-center justify-center rounded-full border border-[#FF2E63]/70 bg-[#FF2E63]/10 transition-all hover:bg-[#FF2E63]/20 md:h-[68px] md:w-[68px]"
              >
                <Play className="h-5 w-5 translate-x-[2px] fill-[#FF2E63] text-[#FF2E63] md:h-6 md:w-6" />
              </a>
              <p className="mt-3 font-cinzel text-[10px] font-semibold text-white/70 md:mt-4 md:text-[11px]">
                {pageUi.trailerLabel}
              </p>
            </motion.div>
          </div>
        </div>

        {/* Bottom timeline — dots ON the curve, labels follow the arc near each dot */}
        <div className="blueberry-timeline-wrap absolute inset-x-0 bottom-5 z-20 md:bottom-10">
          <div className="relative mx-auto max-w-[1500px] px-5 md:px-10 lg:px-16">
            <div
              className="blueberry-timeline-shell relative"
              style={{ height: `${TIMELINE_PEAK + 110}px` }}
            >
              {/* Arc guide — matches dot math exactly */}
              <svg
                viewBox={`0 0 100 ${TIMELINE_PEAK}`}
                preserveAspectRatio="none"
                className="absolute inset-x-0 top-0 w-full"
                style={{ height: `${TIMELINE_PEAK}px` }}
                aria-hidden="true"
              >
                <defs>
                  <linearGradient id="tlGrad" x1="0" x2="1" y1="0" y2="0">
                    <stop offset="0%" stopColor="#FF2E63" stopOpacity="0.18" />
                    <stop offset="50%" stopColor="#FF2E63" stopOpacity="0.85" />
                    <stop
                      offset="100%"
                      stopColor="#FF2E63"
                      stopOpacity="0.18"
                    />
                  </linearGradient>
                </defs>
                <path
                  d={`M 0 0 Q 50 ${TIMELINE_PEAK * 2} 100 0`}
                  fill="none"
                  stroke="url(#tlGrad)"
                  strokeWidth="0.6"
                  vectorEffect="non-scaling-stroke"
                />
              </svg>

              {/* Chapter stacks — each one is positioned where its dot meets the curve.
                  Labels sit just below the dot, so the whole stack follows the arc. */}
              <div role="list" aria-label={pageUi.storyListLabel}>
                {storyChapters.map((ch, i) => {
                  const xPct = (i / (storyChapters.length - 1)) * 100;
                  const y = dotY(i, storyChapters.length);
                  const isActive = i === active;
                  const isVisited = i < active;
                  const isLit = isActive || isVisited;

                  return (
                    <div
                      key={ch.id}
                      role="listitem"
                      aria-label={`${pageUi.chapterAriaPrefix} ${ch.id} - ${ch.label}`}
                      aria-current={isActive ? "step" : undefined}
                      className="blueberry-chapter-link chapter-link group absolute flex cursor-default flex-col items-center text-center"
                      style={{
                        left: `${xPct}%`,
                        top: `${y - DOT_BOX / 2}px`,
                        transform: "translateX(-50%)",
                      }}
                    >
                      {/* Dot wrapper — fixed 14px box so optical center always sits on the curve */}
                      <span
                        className="relative flex items-center justify-center"
                        style={{
                          width: `${DOT_BOX}px`,
                          height: `${DOT_BOX}px`,
                        }}
                      >
                        {isActive && (
                          <span
                            aria-hidden="true"
                            className="timeline-ping absolute left-1/2 top-1/2 block h-[12px] w-[12px] rounded-full border border-[#FF2E63]"
                          />
                        )}
                        <span
                          className={`chapter-dot block rounded-full transition-all ${
                            isActive
                              ? "timeline-active-dot h-[12px] w-[12px] border-2 border-[#FF2E63] bg-[#FF2E63]"
                              : isVisited
                                ? "timeline-visited-dot h-[9px] w-[9px] border border-[#FF2E63] bg-[#FF2E63]/85"
                                : "h-[8px] w-[8px] border border-white/40 bg-transparent"
                          }`}
                        />
                      </span>

                      {/* Labels — close to dot, so the stack follows the arc */}
                      <span
                        className={`mt-2 font-serif-display text-[14px] font-semibold leading-none transition-colors md:text-[18px] ${
                          isLit ? "cinematic-red" : "text-white/85"
                        } ${isVisited && !isActive ? "opacity-80" : ""}`}
                      >
                        {ch.id}
                      </span>
                      <span
                        className={`mt-1 hidden whitespace-nowrap font-cinzel text-[9.5px] font-semibold transition-colors md:block ${
                          isLit ? "cinematic-red" : "text-white/70"
                        } ${isVisited && !isActive ? "opacity-80" : ""}`}
                      >
                        {ch.label}
                      </span>
                      <span
                        className={`mt-1 hidden whitespace-nowrap text-[11.5px] tracking-wide transition-colors md:block ${
                          isLit ? "cinematic-red" : "text-white/55"
                        } ${isVisited && !isActive ? "opacity-80" : ""}`}
                      >
                        {ch.sub}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default BlueberrySenseiPage;
